import { Box, Divider, SelectChangeEvent } from "@mui/material";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import AutoScrollWrapper from "../../../../../../components/common/AutoScrollWrapper/AutoScrollWrapper.tsx";
import { DropDownItem } from "../../../../../../components/common/BriaDropDown/BriaDropDown.tsx";
import { APPS } from "../../../../../../constants/AppsConstants.ts";
import { useAppStore } from "../../../../../../hooks/useStores.tsx";
import InputLayout from "../../../../../../layout/InputLayout/InputLayout";
import { ImageEditingViewMode } from "../../../../../../models/image-to-image.ts";
import { TextToImageConfigType } from "../../../../../../models/text-to-image.ts";
import TextToImageSteps from "../../../../../Playground/TextToImage/Config/Advanced/Steps/TextToImageSteps.tsx";
import TextToImageGenerateButton from "../../../../../Playground/TextToImage/Generator/Search/Button/TextToImageGenerateButton.tsx";
import TgInfluenceSlider from "../../../../App/Image/Config/Influence/TgInfluenceSlider.tsx";
import TgPromptInput from "../../../../App/Video/Config/PromptInput/TgPromptInput.tsx";
import NewTgDropdown from "../../../Dropdown/NewTgDropdown.tsx";
import { TgModel } from "../../../store/tg-model-store.tsx";
import TgConfigAdvanced from "./Advanced/TgConfigAdvanced.tsx";
import CompareModels from "./CompareModels/CompareModels.tsx";
import styles from "./TgEvaluationConfig.module.scss";

const TgEvaluationConfig = () => {
	const { t } = useTranslation("tg", { keyPrefix: "models.viewer.evaluation.config" });
	const location = useLocation();
	const { playgroundStore, textToImageStore, tgStore } = useAppStore();
	const { tgModelPlaygroundStore, tgModelStore } = tgStore;
	const { config, handleConfigChange } = textToImageStore;
	const inputsContainerRef = useRef<HTMLDivElement | null>(null);

	const [mainModelId, setMainModelId] = useState<string>("");

	useEffect(() => {
		runInAction(() => {
			playgroundStore.handleAppChange(APPS.TG_IMAGE_GENERATION);
			playgroundStore.imageEditingViewMode = ImageEditingViewMode.GALLERY;
			textToImageStore.errors = {};
			textToImageStore.IpSignalWarning = false;
		});
	}, []);

	useEffect(() => {
		textToImageStore.imageGenerationContainerRef = inputsContainerRef;
	}, [inputsContainerRef]);

	useEffect(() => {
		const modelToView = location.state?.modelToView || tgModelStore.modelToView;
		if (modelToView.id) {
			handleModelChange({} as SelectChangeEvent<string>, {
				key: modelToView.name,
				extraData: modelToView,
				value: modelToView.id,
			});
		}
	}, [location.state?.modelToView?.id, tgModelStore.modelToView?.id]);

	const handleModelChange = (_e: SelectChangeEvent<string>, selectedItem: DropDownItem<string, TgModel>) => {
		const { key, extraData } = selectedItem;
		const { id, training_version, generation_prefix, active_model_version } = extraData || ({} as any);

		setMainModelId(id?.toString() || "");
		runInAction(() => {
			textToImageStore.config = {
				...textToImageStore.config,
				model: "",
				model_version: "",
				model_name: key,
				model_id: "",
				tailored_model_id: id?.toString(),
				checkpoint_number: active_model_version,
				training_version,
				generation_prefix,
				include_generation_prefix: false,
				negative_prompt: undefined,
				content_moderation: false,
				prompt_enhancement: false,
			};
		});
	};

	const isCompareDifferentModels =
		new Set([
			textToImageStore.config?.tailored_model_id,
			...tgModelPlaygroundStore.compareModels.map((m) => m?.id?.toString()).filter((m) => !!m),
		]).size > 1;

	const generateCompareModels = async () => {
		const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
		const modelsQueue = tgModelPlaygroundStore.compareModels;

		for (let i = 0; i < modelsQueue.length; i++) {
			const model = modelsQueue[i];
			if (!model?.id) continue;

			const checkpoint = model.active_model_version ? String(model.active_model_version) : undefined;
			const config: TextToImageConfigType = {
				...textToImageStore.config,
				tailored_model_id: model.id.toString(),
				checkpoint_number: checkpoint,
				model_name: model.name,
				generation_prefix: model.generation_prefix,
				seed: (
					playgroundStore.playgroundResults[playgroundStore.playgroundResults.length - 1]
						.config as TextToImageConfigType
				).seed,
			};

			textToImageStore.generateTextToImage(config, true);

			if (i < modelsQueue.length - 1) {
				await sleep(1000);
			}
		}
	};

	const handleGenerate = async () => {
		await textToImageStore.generateTextToImage(undefined, true);
		generateCompareModels();
	};

	const getPromptDescription = () => {
		if (isCompareDifferentModels) {
			return t("prompt.description");
		} else if (textToImageStore.config.generation_prefix) {
			return t("prompt.withPrefixDescription");
		}

		return undefined;
	};

	return (
		<Box className={styles.configsWrapper}>
			<Box className={styles.config}>
				<AutoScrollWrapper scrollContainer={inputsContainerRef}>
					<Box ref={inputsContainerRef} className={styles.inputs}>
						<InputLayout label={t("model.label")}>
							<NewTgDropdown
								value={mainModelId}
								onChange={handleModelChange}
								showCheckpoints
								placeholder={t("model.placeholder")}
								alwaysRefresh
								hideTrainButton
							/>
						</InputLayout>
						<CompareModels />
						<Divider className={styles.divider} />
						<InputLayout
							label={t("prompt.label")}
							description={getPromptDescription()}
							descriptionPosition="bottom"
						>
							<TgPromptInput
								prefix={isCompareDifferentModels ? "" : config?.generation_prefix}
								prompt={config?.prompt}
								placeholder={
									isCompareDifferentModels ? t("prompt.emptyPrompt") : t("prompt.noModelPlaceholder")
								}
								onChange={(newPrompt) => handleConfigChange("prompt", newPrompt)}
								onEnter={handleGenerate}
								disabled={!config?.tailored_model_id}
								maxRows={8}
							/>
						</InputLayout>

						<TgInfluenceSlider />
						<TextToImageSteps />

						<Divider className={styles.divider} />
						<TgConfigAdvanced scrollContainer={inputsContainerRef} />
					</Box>
				</AutoScrollWrapper>
			</Box>
			<TextToImageGenerateButton
				className={styles.primaryBtn}
				handleGenerate={handleGenerate}
				isTgEvaluation={true}
			/>
		</Box>
	);
};

export default observer(TgEvaluationConfig);
